import { Grid, useTheme, CardContent, Typography, Paper } from "@mui/material";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector } from "react-redux";

interface IAdminHomeProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal) => void;
  permissions: any;
  setTabValue: any;
  /** Whether the Gendecs plugin is deployed for this customer (controls the GenDec card). */
  gendecAvailable?: boolean;
}

/**
 * Manage Custom Airports
 * @param props -
 */
export const AdminHome = (props: IAdminHomeProps): JSX.Element => {
  const { id, setActiveModal, permissions, setTabValue, gendecAvailable } = props;
  const isAPISCustomer = useSelector(
    (state: IRootState) =>
      import.meta.env.MODE === "development" ||
      state.structures.customerStructure.globalAppConfig?.customer?.settings?.isAPISEnabled
  );
  const theme = useTheme();
  const paperStyle = {
    border: `1px solid ${theme.palette.primary.dark}`,
    padding: 1,
    transition: "background-color 350ms",
    minHeight: 80,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      transition: "background-color 700ms",
    },
  };

  const tabList = [
    {
      title: "Users",
      desc: "Manage OC users. (All crew must be a user first)",
      permission: permissions["ManageUsers"],
      order: 1,
      tabValue: "ManageUsers",
    },
    {
      title: "Crew",
      desc: "Manage Crew Members",
      permission: permissions["ManageCrew"],
      order: 2,
      tabValue: "ManageCrew",
    },
    {
      title: "Crew Schedule",
      desc: "Update the release schedules for each crew group",
      permission: permissions["ManageCrewSchedule"],
      order: 3,
      tabValue: "ManageCrewSchedule",
    },
    {
      title: "Crew Devices",
      desc: "Add, and Manage Crews acceptable devices here",
      permission: permissions["ManageCrewDevices"],
      order: 4,
      tabValue: "ManageCrewDevices",
    },
    {
      title: "User Devices",
      desc: "Add, and Manage Non-Crews acceptable devices here",
      permission: permissions["ManageUserDevices"],
      order: 5,
      tabValue: "ManageUserDevices",
    },
    {
      title: "Aircraft",
      desc: "Manage Configurations & Revisions",
      permission: permissions["AircraftTab"],
      order: 6,
      tabValue: "AircraftTab",
    },
    {
      title: "Aircraft Groups",
      desc: "Manage Aircraft Groups",
      permission: permissions["AircraftGroupsTab"],
      order: 7,
      tabValue: "AircraftGroupsTab",
    },
    {
      title: "Airports",
      desc: "Manage the list of acceptable airports with OC",
      permission: permissions["AirportsTab"],
      order: 8,
      tabValue: "AirportsTab",
    },
    {
      title: "Customers",
      desc: "Manage Customer Lists in OC",
      permission: permissions["CustomersTab"],
      order: 9,
      tabValue: "CustomersTab",
    },
    {
      title: "Dispatch Desks",
      desc: "Create, edit and delete dispatch desks",
      permission: permissions["ManageDispatchDesks"],
      order: 10,
      tabValue: "ManageDispatchDesks",
    },
  ];

  if (isAPISCustomer) {
    tabList.push({
      title: "APIS",
      desc: "APIS Management",
      permission: permissions["ManageAPIS"],
      order: 11,
      tabValue: "ManageAPIS",
    });
  }

  // Only when the Gendecs plugin is deployed for this customer; the card still needs the permission.
  if (gendecAvailable) {
    tabList.push({
      title: "GenDec",
      desc: "Manage GenDec recipients and send rules",
      permission: permissions["ManageGenDecs"],
      order: 12,
      tabValue: "ManageGenDecs",
    });
  }

  return (
    <CardContent>
      <Grid container spacing={1} columns={10}>
        {tabList
          .filter((tab) => tab.permission)
          .sort((a, b) => a.title.localeCompare(b.title))
          .map((tab, tabIdx) => {
            return (
              <Grid item xs={2} key={tabIdx} onClick={() => setTabValue(tab.tabValue)}>
                <Paper elevation={4} sx={paperStyle}>
                  <Typography sx={{ fontWeight: 900 }}>{tab.title}</Typography>
                  <Typography sx={{ fontSize: "0.8rem" }}>{tab.desc}</Typography>
                </Paper>
              </Grid>
            );
          })}
      </Grid>
    </CardContent>
  );
};
